// ============================================
// pages/Publish.tsx
//
// 发布表单. 把 Studio 分享过来的草稿 (sessionStorage) 填成完整作品并提交审核.
//
// 接 community-publish-project (Week 1 已就绪).
//
// Week 2 重做要点 (官网设计系统):
//   - 套 Layout (窄容器)
//   - 来自 Studio 的只读原料卡 (板型/积木数/代码长度/模式)
//   - 表单: 标题 / 简介 / 封面URL / 分类 / 难度 / 标签 chips / 教程 / 下载策略 / 付费金额
//   - 标签: chip 输入 (回车添加, 上限 8 个)
//   - 难度: 三选一 pill
//   - 付费金额: 仅 paid_download 时显示
//   - 字段校验沿用 Week 1 规则 (标题≤100, 简介≥20)
// ============================================

import { useEffect, useState } from 'react';
import { Puzzle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import CoverUploader from '../components/CoverUploader';
import { callApi } from '../services/api';
import { loadDraft, clearDraft } from '../services/draft';
import { loadCategories, getCategoriesSync, type Category } from '../services/categories';
import type { SharedDraft, ProjectCategory, ProjectDifficulty, DownloadPolicy } from '../types/community';

const DIFFICULTIES: Array<{ id: ProjectDifficulty; label: string; desc: string }> = [
  { id: 'beginner', label: '入门', desc: '照着搭就能成' },
  { id: 'intermediate', label: '进阶', desc: '需要一点基础' },
  { id: 'advanced', label: '高级', desc: '有挑战性' },
];

const POLICIES: Array<{ id: DownloadPolicy; label: string; desc: string }> = [
  { id: 'public_view_only', label: '仅展示', desc: '别人只能看,不能下载' },
  { id: 'free_download', label: '免费下载', desc: '任何人都能免费下载' },
  { id: 'paid_download', label: '付费下载', desc: '下载需消耗积分' },
];

const TAG_MAX = 8;
const TAG_LEN = 12;

export default function Publish() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<SharedDraft | null>(null);
  const [draftMissing, setDraftMissing] = useState(false);

  // 表单字段
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [category, setCategory] = useState<ProjectCategory>('');
  const [categories, setCategories] = useState<Category[]>(getCategoriesSync());
  const [difficulty, setDifficulty] = useState<ProjectDifficulty>('beginner');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [tutorial, setTutorial] = useState('');
  const [downloadPolicy, setDownloadPolicy] = useState<DownloadPolicy>('free_download');
  const [paidCost, setPaidCost] = useState(10);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = loadDraft();
    if (!d) {
      setDraftMissing(true);
      return;
    }
    setDraft(d);
    // 标题留空,引导用户主动起名(Studio 项目名带"项目:"前缀,不适合直接当作品标题)
    setTitle('');

    // 分类: 后端定义. 拉取后默认选中第一个 (用户未手选时).
    loadCategories().then((cats) => {
      setCategories(cats);
      setCategory((cur) => cur || cats[0]?.id || '');
    });
  }, []);

  function addTag() {
    const t = tagInput.trim().replace(/^#/, '');
    if (!t) return;
    if (t.length > TAG_LEN) { setError(`单个标签不超过 ${TAG_LEN} 字`); return; }
    if (tags.includes(t)) { setTagInput(''); return; }
    if (tags.length >= TAG_MAX) { setError(`最多 ${TAG_MAX} 个标签`); return; }
    setTags([...tags, t]);
    setTagInput('');
    setError(null);
  }

  function removeTag(t: string) {
    setTags(tags.filter((x) => x !== t));
  }

  async function onSubmit() {
    setError(null);
    if (!title.trim()) { setError('请填写标题'); return; }
    if (title.length > 100) { setError('标题不超过 100 字'); return; }
    if (description.trim().length < 20) { setError('简介至少 20 个字'); return; }
    if (!coverUrl.trim()) { setError('请上传封面图'); return; }
    if (!category) { setError('请选择分类'); return; }
    if (downloadPolicy === 'paid_download' && (paidCost < 1 || paidCost > 9999)) {
      setError('付费金额需在 1–9999 积分之间');
      return;
    }

    setSubmitting(true);
    const r = await callApi<{ short_id: string; status: string }>('community-publish-project', {
      // 原料 (从 draft 透传)
      workspace_xml: draft!.payload.workspace_xml,
      code: draft!.payload.code,
      board_type: draft!.payload.board_type,
      block_count: draft!.payload.block_count,
      // 用户填的
      title: title.trim(),
      description: description.trim(),
      cover_url: coverUrl.trim() || null,
      category,
      tags,
      difficulty,
      tutorial_md: tutorial.trim(),
      download_policy: downloadPolicy,
      paid_download_cost: downloadPolicy === 'paid_download' ? paidCost : 0,
      from_intent_id: draft!.intent_id,
    });
    setSubmitting(false);

    if (!r.ok || !r.short_id) {
      setError(r.message || r.error || '发布失败');
      return;
    }
    clearDraft();
    navigate(`/p/${r.short_id}`, { replace: true });
  }

  // ── 无草稿 ──
  if (draftMissing) {
    return (
      <Layout width="narrow" showSearch={false}>
        <div className="py-20 flex justify-center">
          <div className="bg-white border border-ink-300/40 rounded-3xl shadow-card p-10 max-w-md w-full text-center">
            <div className="flex justify-center mb-4"><Puzzle className="w-12 h-12 text-gold-400" strokeWidth={1.5} /></div>
            <h2 className="text-xl font-bold text-ink-900 mb-3">作品从 HiPiiDo App 来</h2>
            <p className="text-ink-500 mb-3 leading-relaxed text-left">
              创新工场里的作品,都是创作者在 HiPiiDo App 里搭好积木后,点「分享」发布过来的。
            </p>
            <p className="text-ink-500 mb-6 leading-relaxed text-left">
              想发布自己的作品?下载 App,从一颗 LED 开始搭起,用 App 身份登录,不用单独注册 😊
            </p>
            <div className="flex flex-col gap-3">
              <a
                href="https://hipiido.cloud/download"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full"
              >
                下载 HiPiiDo App
              </a>
              <button className="btn-secondary w-full" onClick={() => navigate('/')}>
                先逛逛大家的作品
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  if (!draft) {
    return (
      <Layout width="narrow" showSearch={false}>
        <p className="py-20 text-center text-ink-400">加载中…</p>
      </Layout>
    );
  }

  const codeBytes = draft.payload.code_byte_size || 0;
  const codeBytesText = codeBytes < 1024 ? `${codeBytes} B` : `${(codeBytes / 1024).toFixed(1)} KB`;

  return (
    <Layout width="narrow" showSearch={false}>
      <div className="py-10">
        <span className="kicker"><span className="dot" />发布作品</span>
        <h1 className="grad-ink text-3xl font-black mt-4">分享到 HiPiiDo 创新工场</h1>
        <p className="text-ink-500 mt-2">填好信息提交后,作品将进入审核,通过后出现在作品广场。</p>

        {/* ── 只读:来自 Studio 的原料 ── */}
        <div className="bg-white border border-ink-300/40 rounded-2xl p-5 mt-7">
          <div className="section-num mb-3">FROM STUDIO</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <ReadField label="板型" value={draft.payload.board_type} />
            <ReadField label="积木数量" value={String(draft.payload.block_count)} />
            <ReadField label="代码长度" value={codeBytesText} />
            <ReadField label="模式" value={draft.payload.mode === 'blocks' ? '积木编程' : '代码模式'} />
          </div>
        </div>

        {/* ── 表单 ── */}
        <div className="bg-white border border-ink-300/40 rounded-2xl p-5 sm:p-6 mt-5 space-y-6">
          {/* 标题 */}
          <Field label="标题" required>
            <input
              className="input-field"
              value={title}
              maxLength={100}
              placeholder="给你的作品起个名字"
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>

          {/* 简介 */}
          <Field label="简介" required hint={`${description.length} 字 · 至少 20`}>
            <textarea
              className="input-field min-h-[120px] resize-y"
              value={description}
              maxLength={1000}
              placeholder="介绍一下你的作品:它能做什么?怎么用?(至少 20 个字)"
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>

          {/* 封面 */}
          <Field label="封面图" required hint="必传,发布到列表会显示这张图,建议横版">
            <CoverUploader value={coverUrl} onChange={setCoverUrl} />
          </Field>

          {/* 分类 */}
          <Field label="分类">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <PillBtn key={c.id} active={category === c.id} onClick={() => setCategory(c.id)}>
                  {c.label}
                </PillBtn>
              ))}
            </div>
          </Field>

          {/* 难度 */}
          <Field label="难度">
            <div className="grid grid-cols-3 gap-3">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setDifficulty(d.id)}
                  className={`text-left px-4 py-3 rounded-xl border transition-colors ${
                    difficulty === d.id
                      ? 'bg-gold-soft border-gold-300 text-gold-700'
                      : 'bg-white border-ink-300/40 text-ink-600 hover:border-gold-200'
                  }`}
                >
                  <div className="font-semibold text-sm">{d.label}</div>
                  <div className="text-[11px] text-ink-400 mt-0.5">{d.desc}</div>
                </button>
              ))}
            </div>
          </Field>

          {/* 标签 */}
          <Field label="标签" hint={`${tags.length}/${TAG_MAX}`}>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill bg-gold-soft text-gold-700 text-[13px] border border-gold-200">
                  #{t}
                  <button onClick={() => removeTag(t)} className="text-gold-400 hover:text-gold-700 leading-none">×</button>
                </span>
              ))}
            </div>
            {tags.length < TAG_MAX && (
              <input
                className="input-field"
                value={tagInput}
                placeholder="输入标签后回车添加,如:夜灯、声控"
                maxLength={TAG_LEN}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTag();
                  }
                }}
              />
            )}
          </Field>

          {/* 教程 */}
          <Field label="制作教程" hint="选填,支持换行">
            <textarea
              className="input-field min-h-[120px] resize-y"
              value={tutorial}
              maxLength={5000}
              placeholder="一步步教别人做出来:需要哪些材料、怎么接线、有什么坑…(选填)"
              onChange={(e) => setTutorial(e.target.value)}
            />
          </Field>

          {/* 下载策略 */}
          <Field label="下载策略">
            <div className="space-y-2.5">
              {POLICIES.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setDownloadPolicy(p.id)}
                  className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded-xl border transition-colors ${
                    downloadPolicy === p.id
                      ? 'bg-gold-soft border-gold-300'
                      : 'bg-white border-ink-300/40 hover:border-gold-200'
                  }`}
                >
                  <span className={`tick ${downloadPolicy === p.id ? '' : '!bg-ink-300/40 !text-transparent'}`}>✓</span>
                  <span className="flex-1">
                    <span className={`block text-sm font-semibold ${downloadPolicy === p.id ? 'text-gold-700' : 'text-ink-700'}`}>{p.label}</span>
                    <span className="block text-[11px] text-ink-400 mt-0.5">{p.desc}</span>
                  </span>
                </button>
              ))}
            </div>

            {/* 付费金额 (仅付费下载) */}
            {downloadPolicy === 'paid_download' && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-sm text-ink-600">下载消耗</span>
                <input
                  type="number"
                  className="input-field !w-28"
                  value={paidCost}
                  min={1}
                  max={9999}
                  onChange={(e) => setPaidCost(Math.max(1, Math.min(9999, Number(e.target.value) || 0)))}
                />
                <span className="text-sm text-ink-600">积分</span>
              </div>
            )}
          </Field>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-100 px-4 py-3 rounded-xl">{error}</div>
          )}

          {/* 按钮 */}
          <div className="flex gap-3 pt-2">
            <button className="btn-primary flex-1 py-3" disabled={submitting} onClick={onSubmit}>
              {submitting ? '提交中…' : '提交审核'}
            </button>
            <button className="btn-secondary" disabled={submitting} onClick={() => navigate('/')}>
              取消
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

// ── 小组件 ──
function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold text-ink-800">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {hint && <span className="text-xs text-ink-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-ink-400">{label}</div>
      <div className="font-semibold text-ink-900 text-sm mt-0.5 font-mono">{value}</div>
    </div>
  );
}

function PillBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1.5 rounded-pill text-sm font-medium border transition-colors ${
        active ? 'bg-gold-600 text-white border-gold-600' : 'bg-white text-ink-600 border-ink-300/40 hover:border-gold-200'
      }`}
    >
      {children}
    </button>
  );
}
